import { useState, useRef, useEffect } from 'react';

import { User } from 'firebase/auth';

import CommentData from '../../../../types/commentData';
import Comment from '../../../molecules/comment/Comment';
import CommentForm from '../../../molecules/comment-form/CommentForm';

import './DiscussionThreadContent.scss';

interface DiscussionThreadContentProps {
  question: CommentData;
  responseComments: CommentData[];
  onReply?: (commentBody: string, isAnonymous: boolean, topLevelCommentId: string, replyingToId?: string) => void;
  onThumbsUp?: (comment: CommentData, user: User | null) => void;
  onThumbsDown?: (comment: CommentData, user: User | null) => void;
  onDeleteComment: (comment: CommentData) => void;
}

export default function DiscussionThreadContent({
  question,
  responseComments,
  onReply,
  onThumbsUp,
  onThumbsDown,
  onDeleteComment,
}: DiscussionThreadContentProps) {
  const [replyToComment, setReplyToComment] = useState<CommentData>();

  const repliesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    repliesContainerRef.current?.scrollTo({ top: repliesContainerRef.current.scrollHeight, behavior: 'smooth' });
  }, [responseComments]);

  function handleOnSend(commentBody: string, isAnonymous: boolean) {
    onReply?.(commentBody, isAnonymous, question.id, replyToComment?.id);
  }

  const ResponseComments = responseComments
    .sort((a, b) => b.upUuids.length - a.upUuids.length)
    .map((comment, i) => {
      const repliedToComment = responseComments.find(responseComment => responseComment.id === comment.replyingToId);
      return (
        <Comment
          key={comment.id}
          className="mb-4"
          comment={comment}
          onThumbsUp={onThumbsUp}
          onThumbsDown={onThumbsDown}
          onClickReply={() => setReplyToComment(comment)}
          repliedToComment={repliedToComment}
          onDeleteComment={onDeleteComment}
        />
      );
    });

  return (
    <div className="discussion-thread-content h-full flex flex-col p-6 gap-4">
      <Comment
        onThumbsUp={onThumbsUp}
        onThumbsDown={onThumbsDown}
        onDeleteComment={onDeleteComment}
        comment={question}
      />
      <div className="flex-grow flex flex-col">
        {ResponseComments.length > 0 ? (
          <>
            <p className="text-medium text-center mb-4">Replies</p>
            <div className="replies-container" ref={repliesContainerRef}>
              {ResponseComments}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <h2 className="text-lg font-medium">No replies given, yet.</h2>
            <p className="mb-16">Help out by providing an answer!</p>
          </div>
        )}
      </div>
      <CommentForm
        onSend={handleOnSend}
        placeholder="Answer a question"
        replyToComment={replyToComment}
        onClearReplyingToComment={() => setReplyToComment(undefined)}
      />
    </div>
  );
}
